import React from 'react';
import { EVENT_FORMS, countRu } from '../../../utils/pluralRu';
import { describeTimeInterval, formatPercent } from '../summaryFormatters';
import { VIOLATION_VOCAB } from '../summaryVocab';

/**
 * ТЗ v4 §34 (FE-FIX31): распределение событий по времени суток.
 *
 * Все бизнес-правила (минимальная выборка, шаг 15/30 минут, слияние соседних
 * интервалов, максимум интервалов, точная сумма долей) живут на backend и
 * сюда не копируются. Интервалы рисуются ровно в том виде и порядке, в каком
 * пришли: длинный слитый интервал остаётся одним интервалом.
 *
 * insufficient_data — это не «ноль нарушений» и не повод нарисовать пик:
 * показываем нейтральное состояние и размер выборки.
 *
 * BM v2 §2: компонент поддерживает третью серию — позапрошлый период, если
 * backend её отдал. vocab — имя поля счётчика и подписи вкладки.
 */
const IntervalList = ({ intervals, otherShare, fillClass = '', countKey }) => (
  <ul className="sum-dist">
    {intervals.map((interval) => (
      <li className="sum-dist-row" key={`${interval.start_minute}-${interval.end_minute}`}>
        <span className="sum-dist-label">{describeTimeInterval(interval)}</span>
        <span className="sum-dist-bar">
          <span
            className={`sum-dist-fill${fillClass}`}
            style={{ width: `${Math.min(100, Number(interval.share_percent) || 0)}%` }}
          />
        </span>
        <span className="sum-dist-value">
          {interval[countKey] ?? '—'} · {formatPercent(interval.share_percent)}
        </span>
      </li>
    ))}
    {otherShare > 0 && (
      <li className="sum-dist-row sum-dist-row--other">
        <span className="sum-dist-label">Другие интервалы</span>
        <span className="sum-dist-bar">
          <span className="sum-dist-fill sum-dist-fill--other" style={{ width: `${Math.min(100, otherShare)}%` }} />
        </span>
        <span className="sum-dist-value">{formatPercent(otherShare)}</span>
      </li>
    )}
  </ul>
);

// Заголовок колонки периода с образцом цвета — он же легенда.
const PeriodTitle = ({ title, swatchClass = '' }) => (
  <span className="sum-legend-item"><i className={`sum-legend-swatch${swatchClass}`} />{title}</span>
);

// Интервалы прошлых периодов не совпадают с текущими: у каждого свой список, без выравнивания.
const PastPeriod = ({ distribution, title, emptyText, fillClass, swatchClass, vocab }) => {
  const available = distribution?.state === 'available';
  const intervals = available && Array.isArray(distribution.intervals) ? distribution.intervals : [];
  const other = available ? Number(distribution.other_share_percent) || 0 : 0;
  const hasRows = intervals.length > 0 || other > 0;
  return (
    <div className="sum-time-period">
      <p className="sum-dist-subtitle">
        {hasRows ? (
          <>
            <PeriodTitle title={title} swatchClass={swatchClass} />
            {` · ${countRu(distribution.sample_size ?? 0, EVENT_FORMS)}`}
          </>
        ) : emptyText}
      </p>
      {hasRows && (
        <IntervalList intervals={intervals} otherShare={other} fillClass={fillClass} countKey={vocab.countKey} />
      )}
    </div>
  );
};

const TimeDistributionCard = ({
  distribution, previousDistribution, previousPreviousDistribution, timezoneMode, vocab = VIOLATION_VOCAB,
}) => {
  const perCamera = timezoneMode === 'per_camera';
  const sampleSize = distribution?.sample_size ?? 0;

  if (!distribution || distribution.state !== 'available') {
    return (
      <section className="sum-card">
        <h2 className="sum-card-title">Распределение по времени суток</h2>
        <p className="sum-muted">
          {sampleSize === 0
            ? vocab.emptyText
            : `Недостаточно данных для распределения · ${countRu(sampleSize, EVENT_FORMS)}`}
        </p>
      </section>
    );
  }

  const intervals = Array.isArray(distribution.intervals) ? distribution.intervals : [];
  const otherShare = Number(distribution.other_share_percent) || 0;

  return (
    <section className="sum-card">
      <h2 className="sum-card-title">Распределение по времени суток</h2>
      <p className="sum-muted">
        {countRu(sampleSize, EVENT_FORMS)}
        {distribution.bin_minutes ? ` · шаг ${distribution.bin_minutes} мин` : ''}
        {perCamera ? ' · локальное время камер' : ''}
      </p>
      {/* На всю ширину периоды стоят колонками рядом — их легко сравнить
          построчно; на узком экране колонки уходят одна под другую. */}
      <div className={`sum-time-periods${previousDistribution ? ' sum-time-periods--compare' : ''}`}>
      <div className="sum-time-period">
        {previousDistribution && (
          <p className="sum-dist-subtitle"><PeriodTitle title="Текущий период" /></p>
        )}
        <IntervalList intervals={intervals} otherShare={otherShare} countKey={vocab.countKey} />
        {intervals.length === 0 && otherShare === 0 && (
          <p className="sum-muted">Нет данных</p>
        )}
      </div>
      {previousDistribution && (
        <PastPeriod
          distribution={previousDistribution}
          title="Прошлый период"
          emptyText="В прошлом периоде данных для распределения недостаточно"
          fillClass=" sum-dist-fill--prev"
          swatchClass=" sum-legend-swatch--prev"
          vocab={vocab}
        />
      )}
      {previousDistribution && previousPreviousDistribution && (
        <PastPeriod
          distribution={previousPreviousDistribution}
          title="Позапрошлый период"
          emptyText="В позапрошлом периоде данных для распределения недостаточно"
          fillClass=" sum-dist-fill--prev2"
          swatchClass=" sum-legend-swatch--prev2"
          vocab={vocab}
        />
      )}
      </div>
    </section>
  );
};

export default TimeDistributionCard;
